import React, { useState, useCallback } from "react";
import { toast } from "react-toastify";
import AssignSchools from "./AssignSchools";
import CreateSchedule from "./CreateSchedule";
import VisitsReports from "./VisitsReports";
import { get, post, del } from "../../services/api";

function QAVisitManagement() {
  const [activeTab, setActiveTab] = useState("assign");
  const [assignments, setAssignments] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [qaUsers, setQaUsers] = useState([]);
  const [provinces, setProvinces] = useState([]);
  const [cities, setCities] = useState([]);
  const [centers, setCenters] = useState([]);
  const [qaRoleId, setQaRoleId] = useState(0);
  const [qaDepartmentId, setQaDepartmentId] = useState(0);
  const [priorities, setPriorities] = useState([]);
  
  const [stats, setStats] = useState({
    totalAssignments: 0,
    totalQaUsers: 0,
    totalCenters: 0,
    pendingVisits: 0,
    completedVisits: 0,
  });
  
  const organizationId = localStorage.getItem("organizationId") || 0;
  const token = localStorage.getItem("token");

  // Fetch QA Configuration (Role and Department IDs)
  const fetchQAConfig = useCallback(async () => {
    try {
      // 1. Fetch Role ID for "QA Officer"
      const rolesResponse = await get("Role", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const qaRole = rolesResponse?.find(r => 
        r.name.toLowerCase().includes("qa") && r.name.toLowerCase().includes("officer")
      ) || rolesResponse?.find(r => r.name.toLowerCase().includes("qa")); // Fallback to any QA role
      
      if (qaRole) {
        setQaRoleId(qaRole.id);
      }

      // 2. Fetch Department ID for "QA"
      const deptResponse = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=Department`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      const qaDept = deptResponse?.commonSettingDetail?.find(d => 
        d.value.toLowerCase() === "qa" || d.value.toLowerCase().includes("quality assurance")
      );
      
      if (qaDept) {
        setQaDepartmentId(qaDept.id);
      }
    } catch (error) {
      console.error("Error fetching QA configuration:", error);
    }
  }, [token]);

  // Fetch Provinces
  const fetchProvinces = useCallback(async () => {
    try {
      const response = await get(
        `Dashboard/GetSatateByOrganizationId?organizationId=${organizationId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setProvinces(response || []);
    } catch (error) {
      console.error("Error fetching provinces:", error);
    }
  }, [organizationId, token]);

  // Fetch Cities by Province
  const fetchCities = useCallback(async (provinceCode) => {
    if (!provinceCode) return;
    try {
      const response = await get(`Dashboard/GetCityByStateCode?code=${provinceCode}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCities(response || []);
      setCenters([]); // Clear centers when cities change
    } catch (error) {
      console.error("Error fetching cities:", error);
    }
  }, [token]);

  // Fetch Centers by City
  const fetchCenters = useCallback(async (cityCode) => {
    if (!cityCode) return;
    try {
      const response = await get(
        `Dashboard/GetCenterByCityCode?code=${cityCode}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setCenters(response || []);
    } catch (error) {
      console.error("Error fetching centers:", error);
    }
  }, [token]);

  // Fetch QA Users
  const fetchQAUsers = useCallback(async () => {
    if (!qaRoleId) return; // Wait for roleId to be fetched
    try {
      // If departmentId is not found (0), we still fetch by roleId
      const response = await get(
        `User/GetUsersByRoleIdAndDepartmentId?roleId=${qaRoleId}&departmentId=${qaDepartmentId}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setQaUsers(response || []);
    } catch (error) { 
      console.error("Error fetching QA users:", error);
    }
  }, [token, qaRoleId, qaDepartmentId]);

  // Fetch Assignments List
  const fetchAssignments = useCallback(async () => {
    try {
      const response = await get("QAVisit/assignments", {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const rawData = Array.isArray(response) ? response : (response?.data || []);
      
      // Normalize data to match frontend keys: qaUserId, createdAt, etc.
      const normalizedData = rawData.map(a => ({
        id: a.id,
        qaUserId: a.userId || a.qaUserId, // Map userId to qaUserId
        centerId: a.centerId,
        notes: a.notes,
        // Map any common date field or fallback to current local string
        createdAt: a.createdAt || a.createdOn || a.assignedOn || a.assignedDate || new Date().toLocaleString()
      }));
      
      setAssignments(normalizedData);
    } catch (error) {
      console.error("Error fetching assignments:", error);
    }
  }, [token]);
  
  // Fetch Priority Settings
  const fetchPriorities = useCallback(async () => {
    try {
      const response = await get(
        `CommonSettings/GetCommonSettingDetailByModuleName?moduleName=qaPriority`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPriorities(response?.commonSettingDetail || []);
    } catch (error) {
      console.error("Error fetching priorities:", error);
    }
  }, [token]);

  React.useEffect(() => {
    fetchProvinces();
    fetchQAConfig();
    fetchAssignments();
    fetchPriorities();
  }, [fetchProvinces, fetchQAConfig, fetchAssignments, fetchPriorities]);

  React.useEffect(() => {
    if (qaRoleId) {
      fetchQAUsers();
    }
  }, [qaRoleId, fetchQAUsers]);

  // Calculate stats whenever assignments or schedules change
  React.useEffect(() => {
    setStats(prev => ({
      ...prev,
      totalAssignments: assignments.length,
      totalQaUsers: qaUsers.length,
      totalCenters: centers.length,
      pendingVisits: schedules.filter(s => s.status === "Pending").length,
      completedVisits: schedules.filter(s => s.status === "Completed").length,
    }));
  }, [assignments, schedules, centers, qaUsers]);


  // Assignment Functions
  const handleAddAssignment = useCallback(async (newAssignment) => {
    try {
      const payload = {
        userId: Number(newAssignment.qaUserId),
        centerId: Number(newAssignment.centerId),
        notes: newAssignment.notes
      };

      const response = await post("QAVisit/assign-campus", payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response && response.isSuccess) {
        toast.success(response.message || "Center assigned successfully");
        fetchAssignments(); // Re-fetch to get real database IDs and synchronized state
        return true;
      } else {
        toast.error(response?.message || "Failed to assign center");
        return false;
      }
    } catch (error) {
      console.error("Error assigning campus:", error);
      toast.error("An error occurred during assignment");
      return false;
    }
  }, [assignments.length, token]);

  const handleDeleteAssignment = useCallback(async (assignmentId) => {
    try {
      const response = await get(`QAVisit/assignments?id=${assignmentId}`, null, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response && response.isSuccess) {
        toast.success(response.message || "Assignment removed successfully");
        fetchAssignments(); // Re-fetch to stay synchronized with server
        return true;
      } else {
        toast.error(response?.message || "Failed to remove assignment");
        return false;
      }
    } catch (error) {
      console.error("Error deleting assignment:", error);
      toast.error("An error occurred while deleting the assignment");
      return false;
    }
  }, [token]);

  // Schedule Functions
  const handleAddSchedule = useCallback((newSchedule) => {
    const newId = `SCH-${String(schedules.length + 1).padStart(4, '0')}`;
    const scheduleWithId = {
      ...newSchedule,
      id: newId,
      status: "Pending",
      actualVisitDate: "",
      timeIn: "",
      timeOut: "",
      gpsIn: "",
      gpsOut: "",
      pdfKey: ""
    };
    
    setSchedules(prev => [...prev, scheduleWithId]);
    toast.success("Visit scheduled successfully");
    return true;
  }, [schedules.length]);

  const handleStartVisit = useCallback((scheduleId) => {
    setSchedules(prev =>
      prev.map(s => s.id === scheduleId ? {
        ...s,
        status: "In Progress",
        timeIn: new Date().toLocaleString(),
        gpsIn: "Captured from device",
        actualVisitDate: new Date().toISOString().split('T')[0],
      } : s)
    );
    toast.success("Visit started successfully");
  }, []);

  const handleCompleteVisit = useCallback((scheduleId) => {
    setSchedules(prev =>
      prev.map(s => s.id === scheduleId ? {
        ...s,
        status: "Completed",
        timeOut: new Date().toLocaleString(),
        gpsOut: "Captured from device",
        pdfKey: `QA-REPORT-${Date.now()}`,
      } : s)
    );
    toast.success("Visit completed successfully");
  }, []);

  const handleDeleteSchedule = useCallback((scheduleId) => {
    setSchedules(prev => prev.filter(s => s.id !== scheduleId));
    toast.success("Schedule deleted");
    return true;
  }, []);

  const handleDownloadReport = (schedule) => {
    // Generate PDF report (demo)
    const reportContent = `
      QA VISIT REPORT
      ----------------------
      Schedule ID: ${schedule.id}
      QA User: ${schedule.qaUserName || schedule.qaUserId}
      Center: ${schedule.centerName || schedule.centerId}
      Visit Date: ${schedule.visitDate}
      Status: ${schedule.status}
      Report Key: ${schedule.pdfKey || "N/A"}
      
      This is a demo report. In production, this would be generated by SMS Portal.
    `;

    const blob = new Blob([reportContent], { type: "application/pdf" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `QA_Report_${schedule.id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    toast.info("Downloading report...");
  };

  return (
    <div className="container-fluid py-4" style={{ backgroundColor: '#f3f5f7', minHeight: '100vh' }}>
      <div className="card shadow-sm" style={{ 
        borderRadius: '10px', 
        border: '1px solid #dee2e6',
        overflow: 'hidden'
      }}>
        {/* Card Header */}
        <div className="card-header" style={{ 
          background: 'white', 
          borderBottom: '1px solid #dee2e6',
          padding: '1.5rem'
        }}>
          <div className="d-flex justify-content-between align-items-center">
            <div className="d-flex align-items-center gap-2">
              <i className="fas fa-clipboard-check fs-4" style={{ color: '#b11217' }}></i>
              <div>
                <div className="page-title" style={{ 
                  fontSize: '1.25rem', 
                  fontWeight: 700, 
                  letterSpacing: '.02em' 
                }}>
                  QA Visit Management Module
                </div>
                <div className="text-muted" style={{ fontSize: '.8rem' }}>
                  Assign centers to QA users, schedule QA visits, and manage visit reports (PDF download demo).
                </div>
              </div>
            </div>
            <div className="d-flex align-items-center">
              <button 
                className="toolbar-icon-btn" 
                title="Refresh" 
                onClick={() => toast.info("Refreshing data...")}
                style={{
                  border: 'none',
                  background: '#f3f5f7',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '4px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-sync-alt"></i>
              </button>
              <button 
                className="toolbar-icon-btn" 
                title="Export (dummy)" 
                onClick={() => toast.info("Export is UI-only in this demo.")}
                style={{
                  border: 'none',
                  background: '#f3f5f7',
                  borderRadius: '50%',
                  width: '36px',
                  height: '36px',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  marginLeft: '4px',
                  cursor: 'pointer'
                }}
              >
                <i className="fas fa-file-export"></i>
              </button>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <ul className="nav nav-tabs px-3 pt-3" id="mainTabs" role="tablist">
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === "assign" ? "active" : ""}`}
              style={{
                borderRadius: 0,
                fontSize: '.9rem',
                padding: '.55rem 1.25rem',
                borderTop: activeTab === "assign" ? '3px solid #b11217' : 'none',
                fontWeight: activeTab === "assign" ? 700 : 'normal'
              }}
              onClick={() => setActiveTab("assign")}
            >
              {/* <i className="fas fa-school mr-2"></i> */}
              1) Assign Schools
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === "schedule" ? "active" : ""}`}
              style={{
                borderRadius: 0,
                fontSize: '.9rem',
                padding: '.55rem 1.25rem',
                borderTop: activeTab === "schedule" ? '3px solid #b11217' : 'none',
                fontWeight: activeTab === "schedule" ? 700 : 'normal'
              }}
              onClick={() => setActiveTab("schedule")}
            >
              {/* <i className="fas fa-calendar-alt mr-2"></i> */}
              2) Create Schedule
            </button>
          </li>
          <li className="nav-item" role="presentation">
            <button
              className={`nav-link ${activeTab === "visits" ? "active" : ""}`}
              style={{
                borderRadius: 0,
                fontSize: '.9rem',
                padding: '.55rem 1.25rem',
                borderTop: activeTab === "visits" ? '3px solid #b11217' : 'none',
                fontWeight: activeTab === "visits" ? 700 : 'normal'
              }}
              onClick={() => setActiveTab("visits")}
            >
              {/* <i className="fas fa-file-alt mr-2"></i> */}
              3) Visits & Reports
            </button>
          </li>
        </ul>

        {/* Tab Content */}
        <div className="tab-content p-3">
          <div className={`tab-pane fade ${activeTab === "assign" ? "show active" : ""}`}>
            {activeTab === "assign" && (
              <AssignSchools
                assignments={assignments}
                qaUsers={qaUsers}
                centers={centers}
                provinces={provinces}
                cities={cities}
                stats={stats}
                onFetchCities={fetchCities}
                onFetchCenters={fetchCenters}
                onAddAssignment={handleAddAssignment}
                onDeleteAssignment={handleDeleteAssignment}
              />
            )}
          </div>
          <div className={`tab-pane fade ${activeTab === "schedule" ? "show active" : ""}`}>
            {activeTab === "schedule" && (
              <CreateSchedule
                schedules={schedules}
                assignments={assignments}
                qaUsers={qaUsers}
                centers={centers}
                priorities={priorities}
                onAddSchedule={handleAddSchedule}
                onStartVisit={handleStartVisit}
                onCompleteVisit={handleCompleteVisit}
                onDeleteSchedule={handleDeleteSchedule}
              />
            )}
          </div>
          <div className={`tab-pane fade ${activeTab === "visits" ? "show active" : ""}`}>
            {activeTab === "visits" && (
              <VisitsReports
                schedules={schedules}
                qaUsers={qaUsers}
                centers={centers}
                onDownloadReport={handleDownloadReport}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default QAVisitManagement;